import { Component, ElementRef, HostListener, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { ThemeService, ThemeFamily } from 'src/app/services';

@Component({
  selector: 'theme-switcher',
  standalone: true,
  imports: [CommonModule, DxButtonModule],
  template: `
    <div class="theme-switcher" #root>
      <dx-button
        stylingMode="text"
        icon="palette"
        [hint]="'Tema · ' + currentLabel()"
        (onClick)="togglePopover($event)">
      </dx-button>

      <div class="theme-popover" *ngIf="open()" role="menu">
        <!-- Familias -->
        <div class="theme-popover-section">
          <div class="theme-popover-title">Paleta</div>
          <div class="theme-grid">
            <button
              *ngFor="let f of families"
              type="button"
              class="theme-chip"
              [class.active]="f.id === themeService.family"
              [attr.title]="f.label"
              (click)="pickFamily(f.id)">
              <span class="chip-swatch" [style.background]="f.swatch"></span>
              <span class="chip-label">{{ f.label }}</span>
              <span class="chip-check" *ngIf="f.id === themeService.family">
                <i class="dx-icon dx-icon-check"></i>
              </span>
            </button>
          </div>
        </div>

        <div class="theme-popover-divider"></div>

        <!-- Modo claro/oscuro -->
        <div class="theme-popover-section">
          <div class="theme-popover-title">Apariencia</div>
          <div class="seg-control">
            <button
              type="button"
              class="seg-option"
              [class.active]="themeService.currentTheme === 'light'"
              (click)="setMode('light')">
              <i class="dx-icon dx-icon-sun"></i>
              <span>Claro</span>
            </button>
            <button
              type="button"
              class="seg-option"
              [class.active]="themeService.currentTheme === 'dark'"
              (click)="setMode('dark')">
              <i class="dx-icon dx-icon-moon"></i>
              <span>Oscuro</span>
            </button>
          </div>
        </div>

        <div class="theme-popover-divider"></div>

        <!-- Densidad normal/compact -->
        <div class="theme-popover-section">
          <div class="theme-popover-title">Densidad</div>
          <div class="seg-control">
            <button
              type="button"
              class="seg-option"
              [class.active]="themeService.currentDensity === 'normal'"
              (click)="setDensity('normal')">
              <i class="dx-icon dx-icon-rowfield"></i>
              <span>Normal</span>
            </button>
            <button
              type="button"
              class="seg-option"
              [class.active]="themeService.currentDensity === 'compact'"
              (click)="setDensity('compact')">
              <i class="dx-icon dx-icon-rowproperties"></i>
              <span>Compacta</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Quick toggle modo -->
      <dx-button
        class="theme-button"
        stylingMode="text"
        [icon]="themeService.currentTheme !== 'dark' ? 'moon' : 'sun'"
        [hint]="themeService.currentTheme === 'dark' ? 'Modo claro' : 'Modo oscuro'"
        (onClick)="onToggleMode()">
      </dx-button>
    </div>
  `,
  styles: [`
    .theme-switcher {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      position: relative;
    }

    .theme-popover {
      position: absolute;
      top: calc(100% + 10px);
      right: 36px;
      width: 320px;
      padding: 14px;
      border-radius: 14px;
      z-index: 1500;
      background: var(--card-background, var(--base-bg, #fff));
      border: 1px solid color-mix(in srgb, var(--base-text-color, #333) 12%, transparent);
      box-shadow:
        0 24px 60px rgba(0, 0, 0, 0.18),
        0 4px 14px rgba(0, 0, 0, 0.06),
        0 0 0 1px color-mix(in srgb, var(--crm-accent-1, #1890ff) 6%, transparent) inset;
      animation: pop-in 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
    }
    @keyframes pop-in {
      from { opacity: 0; transform: translateY(-6px) scale(0.98); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }

    .theme-popover-section { display: flex; flex-direction: column; gap: 8px; }

    .theme-popover-divider {
      height: 1px;
      margin: 12px 0;
      background: linear-gradient(90deg,
        transparent,
        color-mix(in srgb, var(--base-text-color, #333) 14%, transparent),
        transparent);
    }

    .theme-popover-title {
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 1.6px;
      text-transform: uppercase;
      color: color-mix(in srgb, var(--base-text-color, #333) 55%, transparent);
      padding: 0 2px 2px;
    }

    /* === Grid de paletas === */
    .theme-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 6px;
    }

    .theme-chip {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      background: color-mix(in srgb, var(--base-text-color, #333) 4%, transparent);
      border: 1px solid color-mix(in srgb, var(--base-text-color, #333) 8%, transparent);
      border-radius: 9px;
      cursor: pointer;
      font: inherit;
      color: var(--base-text-color, #222);
      text-align: left;
      transition: all 0.16s cubic-bezier(0.4, 0, 0.2, 1);
      min-width: 0;
      position: relative;
    }
    .theme-chip:hover {
      background: color-mix(in srgb, var(--crm-accent-1, #1890ff) 8%, transparent);
      border-color: color-mix(in srgb, var(--crm-accent-1, #1890ff) 30%, transparent);
      transform: translateY(-1px);
    }
    .theme-chip.active {
      background: color-mix(in srgb, var(--crm-accent-1, #1890ff) 14%, transparent);
      border-color: var(--crm-accent-1, #1890ff);
      box-shadow: 0 0 0 1px var(--crm-accent-1, #1890ff) inset,
                  0 4px 14px color-mix(in srgb, var(--crm-accent-1, #1890ff) 22%, transparent);
    }

    .chip-swatch {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      flex-shrink: 0;
      box-shadow:
        0 0 0 2px color-mix(in srgb, var(--base-bg, #fff) 80%, transparent),
        0 2px 6px color-mix(in srgb, currentColor 30%, transparent);
    }
    .chip-label {
      flex: 1;
      font-size: 12px;
      font-weight: 500;
      letter-spacing: 0.1px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .chip-check {
      flex-shrink: 0;
      color: var(--crm-accent-1, #1890ff);
      display: inline-flex;
      align-items: center;

      .dx-icon { font-size: 14px; }
    }

    /* === Segmented control (modo / densidad) === */
    .seg-control {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 4px;
      padding: 4px;
      background: color-mix(in srgb, var(--base-text-color, #333) 4%, transparent);
      border: 1px solid color-mix(in srgb, var(--base-text-color, #333) 8%, transparent);
      border-radius: 10px;
    }

    .seg-option {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 8px 10px;
      background: transparent;
      border: none;
      border-radius: 7px;
      cursor: pointer;
      font: inherit;
      font-size: 12.5px;
      font-weight: 500;
      color: color-mix(in srgb, var(--base-text-color, #333) 70%, transparent);
      transition: all 0.16s cubic-bezier(0.4, 0, 0.2, 1);

      .dx-icon { font-size: 14px; }

      &:hover {
        color: var(--base-text-color, #222);
        background: color-mix(in srgb, var(--base-text-color, #333) 6%, transparent);
      }

      &.active {
        background: var(--card-background, var(--base-bg, #fff));
        color: var(--crm-accent-1, #1890ff);
        font-weight: 600;
        box-shadow:
          0 1px 0 color-mix(in srgb, var(--base-text-color, #333) 8%, transparent) inset,
          0 2px 8px color-mix(in srgb, var(--crm-accent-1, #1890ff) 14%, transparent);
      }
    }
  `],
})
export class ThemeSwitcherComponent {
  protected themeService = inject(ThemeService);
  private host = inject(ElementRef);

  open = signal(false);
  families: ThemeFamily[] = this.themeService.getFamilies();

  currentLabel(): string {
    const f = this.families.find(x => x.id === this.themeService.family);
    const dens = this.themeService.currentDensity === 'compact' ? ' · compacto' : '';
    return `${f?.label ?? this.themeService.family} (${this.themeService.currentTheme === 'dark' ? 'oscuro' : 'claro'}${dens})`;
  }

  togglePopover(e?: any): void {
    e?.event?.stopPropagation?.();
    this.open.update(v => !v);
  }

  pickFamily(id: string): void {
    console.info('[theme-switcher] pickFamily →', id);
    this.open.set(false);
    this.themeService.setFamily(id);
  }

  setMode(mode: 'light' | 'dark'): void {
    if (this.themeService.currentTheme === mode) return;
    this.themeService.setMode(mode);
  }

  setDensity(density: 'normal' | 'compact'): void {
    if (this.themeService.currentDensity === density) return;
    this.themeService.setDensity(density);
  }

  onToggleMode = () => this.themeService.switchTheme();

  // Cierra el popover al click afuera.
  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent) {
    if (!this.open()) return;
    if (!this.host.nativeElement.contains(ev.target)) this.open.set(false);
  }
}
